import { ArrowLeft, ArrowRight } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updatePreferences } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
};

const PreferencesForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack
}) => {
  const dispatch = useDispatch();

  const preferences = useSelector(
    (state: any) => state.root.resume.preferences
  );

  const MAX_CHAR_LIMIT = 50;

  const [errors, setErrors] = useState<{
    targetedCompany?: string;
    targetedRole?: string;
  }>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (value.length > MAX_CHAR_LIMIT) return;

    dispatch(
      updatePreferences({
        [name]: value
      })
    );

    if (name === 'targetedCompany' || name === 'targetedRole') {
      setErrors(prev => ({
        ...prev,
        [name]: value.trim() ? '' : 'This field is required'
      }));
    }
  };

  const handleNext = () => {
    const newErrors: { targetedCompany?: string; targetedRole?: string } = {};

    if (!preferences.targetedCompany?.trim()) {
      newErrors.targetedCompany = 'Targeted Company is required';
    }

    if (!preferences.targetedRole?.trim()) {
      newErrors.targetedRole = 'Targeted Role is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext();
    }
  };

  const CharCount = ({ value }: { value: string }) => {
    const len = value?.length || 0;

    return (
      <span
        className={`text-xs ${
          len >= MAX_CHAR_LIMIT ? 'text-red-500' : 'text-gray-400'
        }`}
      >
        {len}/{MAX_CHAR_LIMIT}
      </span>
    );
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>

            <h1 className='text-2xl font-semibold'>{stepName}</h1>

            <p className='mt-1 text-sm text-gray-400'>
              Define your professional aspirations
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-6 font-semibold'>Tailor Your Reach</h3>

          {/* Targeted Company */}
          <div className='mb-6'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>
                Targeted Company <span className='text-red-500'>*</span>
              </label>

              <CharCount value={preferences.targetedCompany || ''} />
            </div>

            <input
              name='targetedCompany'
              maxLength={MAX_CHAR_LIMIT}
              value={preferences.targetedCompany || ''}
              onChange={handleChange}
              placeholder='Enter target company'
              required
              className={`mt-2 w-full rounded-lg border p-3 focus:outline-none
              ${
                errors.targetedCompany ||
                (preferences.targetedCompany || '').length >= MAX_CHAR_LIMIT
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            />

            {errors.targetedCompany && (
              <p className='mt-1 text-xs text-red-500'>
                {errors.targetedCompany}
              </p>
            )}
          </div>

          {/* Targeted Role */}
          <div className='mb-6'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>
                Targeted Role <span className='text-red-500'>*</span>
              </label>

              <CharCount value={preferences.targetedRole || ''} />
            </div>

            <input
              name='targetedRole'
              maxLength={MAX_CHAR_LIMIT}
              value={preferences.targetedRole || ''}
              onChange={handleChange}
              placeholder='Enter target role'
              required
              className={`mt-2 w-full rounded-lg border p-3 focus:outline-none
              ${
                errors.targetedRole ||
                (preferences.targetedRole || '').length >= MAX_CHAR_LIMIT
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            />

            {errors.targetedRole && (
              <p className='mt-1 text-xs text-red-500'>{errors.targetedRole}</p>
            )}
          </div>

          {/* Areas of Interest */}
          <div className='mb-6'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Areas of Interest</label>

              <CharCount value={preferences.areasOfInterest || ''} />
            </div>

            <textarea
              name='areasOfInterest'
              maxLength={MAX_CHAR_LIMIT}
              value={preferences.areasOfInterest || ''}
              onChange={handleChange}
              placeholder='E.g. FinTech, AI, UX Research'
              rows={3}
              className={`mt-2 w-full rounded-lg border p-3 focus:outline-none
              ${
                (preferences.areasOfInterest || '').length >= MAX_CHAR_LIMIT
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            />
          </div>

          {/* Preferred Languages */}
          <div>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>
                Preferred Languages for Work
              </label>

              <CharCount value={preferences.preferredLanguages || ''} />
            </div>

            <input
              name='preferredLanguages'
              maxLength={MAX_CHAR_LIMIT}
              value={preferences.preferredLanguages || ''}
              onChange={handleChange}
              placeholder='English, German'
              className={`mt-2 w-full rounded-lg border p-3 focus:outline-none
              ${
                (preferences.preferredLanguages || '').length >= MAX_CHAR_LIMIT
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            />
          </div>
        </div>

        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={handleNext}
            variant='contained'
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesForm;
